/**
 * A1: the dealership access a user is EXPECTED to have, derived the way the
 * backend derives it (app/identity/tenancy.py). Nobody sets it by hand:
 *
 *   - Edit: a membership on that dealership (tenants_for), and the dealership
 *     has a data source that is not suspended (_servable).
 *   - Read-only: a Super Admin with no dealership membership reaches every
 *     servable source through the staff policy (_reachable), but may not
 *     write there (may_write).
 *   - None: everyone else. A holding membership does not walk down to its
 *     dealerships; Fusable staff without Super Admin reach none.
 *
 * On top: a suspended account, IronSight switched off or a suspended
 * dealership give no access. Super-admin only (D19): it needs GET /tenants.
 * The server's `multi_tenant_routing` flag is not visible to the client; with
 * it off everybody who reaches the home dealership may write, so the result
 * is labelled "expected" in the UI.
 */
import { findNode, flattenOrgs } from './adminConsole';

export const ACCESS_LABEL = { edit: 'Edit', ro: 'Read-only', none: 'None' };

const blockedReason = (user) => {
    if (user.status === 'suspended') return 'Account is deactivated';
    if (!user.apps?.pricepath) return 'IronSight access is switched off';
    return null;
};

/** { [dealerId]: { level: 'edit'|'ro'|'none', why } } for every dealership in the tree. */
export function accessOf(user, orgs, tenants) {
    const dealers = flattenOrgs(orgs).filter((o) => o.type === 'COG');
    const tenantOf = new Map((tenants || []).map((t) => [t.org_id, t]));
    const home = findNode(orgs, user.org_id);
    const homeType = home?.type;
    const memberOf = homeType === 'COG' ? home.id : null;
    const blocked = blockedReason(user);
    const out = {};
    dealers.forEach((d) => {
        const t = tenantOf.get(d.id);
        const servable = t && t.status !== 'suspended';
        let level = 'none';
        let why;
        if (memberOf) {
            if (memberOf === d.id) {
                if (servable) { level = 'edit'; why = 'Member of this dealership'; } else why = t ? 'Member, but the data source is suspended' : 'Member, but no data source is connected yet';
            } else why = 'Not a member';
        } else if (homeType === 'VENDOR' && user.role === 'super_admin') {
            if (servable) { level = 'ro'; why = 'Fusable staff: may look, not change'; } else why = t ? 'Data source is suspended' : 'No data source connected';
        } else if (homeType === 'TOG') {
            why = 'Holding membership does not reach dealership data today';
        } else if (homeType === 'VENDOR') {
            why = 'Fusable staff without Super Admin reach no dealership';
        } else {
            why = 'Not a member';
        }
        if (level !== 'none' && d.status === 'suspended') { level = 'none'; why = 'Dealership is suspended'; }
        if (level !== 'none' && blocked) {
            why = `${blocked} (otherwise ${ACCESS_LABEL[level]})`;
            level = 'none';
        }
        out[d.id] = { level, why };
    });
    return out;
}

/** The headline for one user: edit / ro / none, the dealerships, and why not. */
export function accessSummary(user, orgs, tenants) {
    const access = accessOf(user, orgs, tenants);
    const dealers = flattenOrgs(orgs).filter((o) => o.type === 'COG');
    const edit = dealers.filter((d) => access[d.id].level === 'edit');
    const ro = dealers.filter((d) => access[d.id].level === 'ro');
    const level = edit.length ? 'edit' : ro.length ? 'ro' : 'none';
    const homeType = findNode(orgs, user.org_id)?.type;
    let why = null;
    if (level === 'none') {
        why = blockedReason(user)
            || (homeType === 'TOG' && 'Holding membership reaches no dealership')
            || (homeType === 'VENDOR' && user.role !== 'super_admin' && 'Staff without Super Admin')
            || dealers.map((d) => access[d.id].why).find(Boolean)
            || 'No dealership on IronSight yet';
    }
    return { level, edit, ro, why, access };
}

/** Short chip text: "Edit · Gooseneck Implement", "Read-only · 3 dealerships", "No access". */
export function accessChipText(summary) {
    if (summary.level === 'edit') {
        return `Edit · ${summary.edit.length === 1 ? summary.edit[0].name : `${summary.edit.length} dealerships`}`;
    }
    if (summary.level === 'ro') {
        return `Read-only · ${summary.ro.length} ${summary.ro.length === 1 ? 'dealership' : 'dealerships'}`;
    }
    return 'No access';
}
